import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Calendar, MapPin, Tag, User } from "lucide-react";
import axios from "axios";
import Navbar from "./Navbar";
import Footer from "./Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    axios.get(`${API}/blog/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setPost(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <div className="min-h-screen bg-white"><Navbar /><div className="flex items-center justify-center min-h-screen"><div className="w-8 h-8 border-2 border-[#174358]/20 border-t-[#174358] rounded-full animate-spin" /></div></div>
  );

  if (!post) return (
    <div className="min-h-screen bg-white"><Navbar />
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <h2 className="font-['Cormorant_Garamond'] text-3xl text-[#0D1B2A]">Article not found</h2>
        <Link to="/blog" className="text-sm text-[#174358] hover:underline">Back to Blog</Link>
      </div>
    </div>
  );

  return (
    <div data-testid="blog-post-page" className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Image */}
      <section className="relative h-[400px] md:h-[480px] overflow-hidden">
        <img src={post.image_url} alt={post.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0D1B2A]/80 via-[#0D1B2A]/30 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 z-10 max-w-4xl mx-auto px-6 md:px-12 pb-12">
          <Link to="/blog" data-testid="back-to-blog" className="inline-flex items-center gap-2 text-white/50 hover:text-white/80 text-sm font-medium mb-5 transition-colors">
            <ArrowLeft size={16} /> Blog
          </Link>
          <div className="flex items-center gap-2 mb-4">
            <span className="text-[10px] bg-white/15 text-white/80 px-3 py-1 rounded-full font-semibold uppercase tracking-wider flex items-center gap-1">
              <MapPin size={9} /> {post.destination}
            </span>
            <span className="text-[10px] bg-white/10 text-white/60 px-3 py-1 rounded-full font-semibold uppercase tracking-wider flex items-center gap-1">
              <Tag size={9} /> {post.category}
            </span>
          </div>
          <h1 data-testid="post-title" className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl md:text-5xl font-light text-white leading-tight">{post.title}</h1>
        </div>
      </section>

      {/* Article Meta */}
      <section className="border-b border-[#0D1B2A]/06">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-5 flex items-center gap-6 text-xs text-[#0D1B2A]/40">
          <span className="flex items-center gap-1.5"><User size={12} /> {post.author}</span>
          <span className="flex items-center gap-1.5"><Calendar size={12} /> {post.created_at ? new Date(post.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : ""}</span>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-12 md:py-16 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <p className="text-base text-[#0D1B2A]/70 font-medium leading-relaxed mb-8 border-l-4 border-[#174358]/20 pl-5">{post.excerpt}</p>
          <div className="prose prose-sm max-w-none text-[#0D1B2A]/60 leading-relaxed space-y-4">
            {post.content.split('\n').filter(Boolean).map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
        </div>
      </article>

      {/* Bottom CTA */}
      <section className="py-12 px-6 md:px-12 bg-[#F8FAFB] border-t border-[#0D1B2A]/05">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-[#0D1B2A] mb-1">Interested in {post.destination}?</p>
            <p className="text-xs text-[#0D1B2A]/40">Explore our full {post.destination} destination gateway for live rates and packages.</p>
          </div>
          <div className="flex gap-3">
            <Link to={`/destination/${encodeURIComponent(post.destination)}`} className="bg-[#174358] text-white hover:bg-[#1e5a75] rounded-full px-6 py-2.5 text-xs font-semibold transition-colors">
              View {post.destination} Gateway
            </Link>
            <Link to="/blog" className="border border-[#0D1B2A]/15 text-[#0D1B2A]/60 hover:text-[#0D1B2A] rounded-full px-6 py-2.5 text-xs font-semibold transition-colors">
              More Articles
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
